import { defineComponent as _defineComponent } from 'vue'
import { renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, renderSlot as _renderSlot, normalizeClass as _normalizeClass, createElementVNode as _createElementVNode, resolveComponent as _resolveComponent, normalizeStyle as _normalizeStyle, createVNode as _createVNode, createCommentVNode as _createCommentVNode } from "vue"

const _hoisted_1 = { class: "BaseCarousel__dots row justify-center items-center gap-sm q-mt-md" }
const _hoisted_2 = { class: "flex gap-xs" }
const _hoisted_3 = ["onClick"]

import { ref, onMounted, onUnmounted, watch, nextTick } from 'vue'
import EmblaCarousel from 'embla-carousel'
import type { EmblaCarouselType, EmblaOptionsType } from 'embla-carousel'
import Autoplay from 'embla-carousel-autoplay'

interface IProps {
  carousels: Record<string, unknown>[]
  navigator?: boolean
  navigatorOnHover?: boolean
  slideClass?: string | Record<string, boolean>
  slideBoxClass?: string | Record<string, boolean>
  options?: EmblaOptionsType
  dots?: boolean
  autoplay?: boolean
}

export default /*@__PURE__*/_defineComponent({
  __name: 'BaseCarousel',
  props: {
    carousels: {},
    navigator: { type: Boolean, default: false },
    navigatorOnHover: { type: Boolean, default: false },
    slideClass: { default: '' },
    slideBoxClass: { default: '' },
    options: { default: () => ({ containScroll: 'trimSnaps' }) },
    dots: { type: Boolean, default: true },
    autoplay: { type: Boolean, default: false }
  },
  setup(__props: any, { expose: __expose }) {

const props = __props

const carouselRef = ref<HTMLElement | null>(null)
const emblaRef = ref<EmblaCarouselType | null>(null)
const isHoverRef = ref<boolean>(false)

const selectedIndexRef = ref(0)
const canScrollRef = ref({ next: false, prev: false })
const slideCountRef = ref(0)

const onSelect = () => {
  if (emblaRef.value) {
    selectedIndexRef.value = emblaRef.value.selectedScrollSnap()
  }
}
const onScroll = () => {
  if (emblaRef.value) {
    canScrollRef.value.next = emblaRef.value.canScrollNext()
    canScrollRef.value.prev = emblaRef.value.canScrollPrev()
  }
}
function onResize() {
  if (emblaRef.value) {
    slideCountRef.value = emblaRef.value.slideNodes().length
  }
}

watch(
  () => props.carousels,
  async () => {
    await nextTick()
    onSelect()
    onScroll()
    onResize()
  },
  { deep: true, immediate: true },
)

onMounted(() => {
  if (carouselRef.value) {
    emblaRef.value = EmblaCarousel(
      carouselRef.value,
      props.options,
      props.autoplay
        ? [
            Autoplay({
              playOnInit: props.autoplay,
              delay: 5000,
              stopOnFocusIn: false,
              stopOnInteraction: false,
              stopOnMouseEnter: true,
              stopOnLastSnap: false,
            }),
          ]
        : undefined,
    )

    slideCountRef.value = emblaRef.value.slideNodes().length
    emblaRef.value.on('select', onSelect)
    emblaRef.value.on('scroll', onScroll)
    onSelect()
    onScroll()
    onResize()
  }
})

onUnmounted(() => {
  emblaRef.value?.destroy()
})

__expose({
  scrollProgress: () => emblaRef.value?.scrollProgress(),
  canScrollNext: () => emblaRef.value?.canScrollNext(),
})

return (_ctx: any,_cache: any) => {
  const _component_k_btn = _resolveComponent("k-btn")!
  const _component_k_icon = _resolveComponent("k-icon")!

  return (_openBlock(), _createElementBlock("div", {
    ref_key: "carouselRef",
    ref: carouselRef,
    class: "BaseCarousel relative-position overflow-hidden relative",
    onMouseenter: _cache[0] || (_cache[0] = ($event: any) => (isHoverRef.value = true)),
    onMouseleave: _cache[1] || (_cache[1] = ($event: any) => (isHoverRef.value = false))
  }, [
    _createElementVNode("div", {
      class: _normalizeClass(["flex no-wrap", __props.slideBoxClass])
    }, [
      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(__props.carousels, (carousel, index) => {
        return (_openBlock(), _createElementBlock("div", {
          key: index,
          class: _normalizeClass(["BaseCarousel__slide flex", [{ 'BaseCarousel__slide--full': !__props.options.dragFree }, __props.slideClass]])
        }, [
          _renderSlot(_ctx.$slots, "carousel", { carousel: carousel })
        ], 2))
      }), 128))
    ], 2),
    (__props.navigator)
      ? _renderSlot(_ctx.$slots, "navigator", {
          key: 0,
          next: emblaRef.value?.scrollNext,
          prev: emblaRef.value?.scrollPrev,
          scrollTo: emblaRef.value?.scrollTo,
          canScroll: canScrollRef.value
        }, () => [
          _createVNode(_component_k_btn, {
            class: _normalizeClass(["BaseCarousel__nav BaseCarousel__nav--left", { 'BaseCarousel__nav--hidden': (__props.navigatorOnHover && !isHoverRef.value) || !canScrollRef.value.prev }]),
            iconable: "",
            icon: "arrow-left",
            style: _normalizeStyle(_ctx.$q.screen.gt.sm ? 'width: 48px; height: 48px' : 'width: 36px; height: 36px'),
            size: _ctx.$q.screen.gt.sm ? 'md' : 'sm',
            disabled: !canScrollRef.value.prev,
            onClick: emblaRef.value?.scrollPrev
          }, null, 8, ["class", "style", "size", "disabled", "onClick"]),
          _createVNode(_component_k_btn, {
            class: _normalizeClass(["BaseCarousel__nav BaseCarousel__nav--right", { 'BaseCarousel__nav--hidden': (__props.navigatorOnHover && !isHoverRef.value) || !canScrollRef.value.next }]),
            iconable: "",
            icon: "arrow-right",
            style: _normalizeStyle(_ctx.$q.screen.gt.sm ? 'width: 48px; height: 48px' : 'width: 36px; height: 36px'),
            size: _ctx.$q.screen.gt.sm ? 'md' : 'sm',
            disabled: !canScrollRef.value.next,
            onClick: emblaRef.value?.scrollNext
          }, null, 8, ["class", "style", "size", "disabled", "onClick"])
        ])
      : _createCommentVNode("", true),
    (__props.dots && slideCountRef.value > 1)
      ? _renderSlot(_ctx.$slots, "dots", {
          key: 1,
          next: emblaRef.value?.scrollNext,
          prev: emblaRef.value?.scrollPrev,
          scrollTo: emblaRef.value?.scrollTo
        }, () => [
          _createElementVNode("div", _hoisted_1, [
            _createVNode(_component_k_icon, {
              class: "cursor-pointer",
              name: "arrow-left",
              color: !emblaRef.value?.canScrollPrev() ? 'cool-gray-70' : 'cool-gray-100',
              size: "12px",
              onClick: emblaRef.value?.scrollPrev
            }, null, 8, ["color", "onClick"]),
            _createElementVNode("div", _hoisted_2, [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(slideCountRef.value, (_, index) => {
                return (_openBlock(), _createElementBlock("span", {
                  key: index,
                  class: _normalizeClass(["BaseCarousel__dot cursor-pointer", { 'BaseCarousel__dot--active': index === selectedIndexRef.value }]),
                  onClick: ($event: any) => (emblaRef.value?.scrollTo(index))
                }, null, 10, _hoisted_3))
              }), 128))
            ]),
            _createVNode(_component_k_icon, {
              class: "cursor-pointer",
              name: "arrow-right",
              color: !emblaRef.value?.canScrollNext() ? 'cool-gray-70' : 'cool-gray-100',
              size: "12px",
              onClick: emblaRef.value?.scrollNext
            }, null, 8, ["color", "onClick"])
          ])
        ])
      : _createCommentVNode("", true)
  ], 544))
}
}

})